import React, {
    ReactElement, useEffect, useState,
} from "react";
import { IoMdLogIn } from "react-icons/io";
import { IdentityProvider, useLoginMutation, useLoginWithProviderLazyQuery } from "../../../__generated__/api";
import { HtmlRenderer } from "../../../components/html-render/HtmlRenderer";
import TimedMessage from "../../../components/timed-message/TimedMessage";
import usePortal from "../../../contexts/PortalContext";
import useRouting from "../../../contexts/Routing";
import useCurrentSession from "../../../contexts/UserContext";
import "./Login.css";
import isAllowedRedirect from "../../../redirect";

export default function Login(): ReactElement {
    const { clearCache } = useCurrentSession();
    const {
        clearCache: clearPortalCache, title, welcomeMessage, loginUrl,
    } = usePortal();

    const [loginWithProvider] = useLoginWithProviderLazyQuery();

    const {
        Paths, getSearchParams, historyPushSearch,
    } = useRouting();

    const searchParams = getSearchParams(Paths.LOGIN);
    const { error: ssoLoginError, redirect } = searchParams;

    const loginProvider = async (provider: IdentityProvider) => {
        let redirectUrl = redirect ?? `https://${window.location.host}`;
        if (!isAllowedRedirect(redirectUrl)) {
            redirectUrl = `https://${window.location.host}`;
        }
        const results = await loginWithProvider({
            variables: {
                provider: {
                    provider,
                    redirectUrl,
                },
            },
        });

        const url = results.data?.loginWithIdentityProvider.url;
        if (!url) {
            return;
        }
        clearCache();
        clearPortalCache();
        window.location.href = url;
    };

    return (
        <div className="full-page">

            <TimedMessage
                shouldShow={ssoLoginError != null && ssoLoginError !== ""}
                title="Error loging in"
                variant="danger"
                onClose={() => {
                    delete (searchParams as Partial<typeof searchParams>).error;
                    historyPushSearch(searchParams);
                }}
            >
                {ssoLoginError}
            </TimedMessage>

            <div className="centered-container pt-5 flex-column flex-lg-row align-items-start">
                <div className="w-100 w-lg-50 border-end-lg pe-3 me-3 d-block">
                    <h5>
                        Welcome to
                        {" "}
                        {title}
                    </h5>
                    <div className="mt-3">
                        {welcomeMessage && (
                            <HtmlRenderer html={welcomeMessage} />
                        )}
                        <small>
                            To sign this portal, please choose from one of the sign in options
                            {" "}
                            <span className="d-none d-lg-inline">to the right</span>
                            <span className="d-lg-none">below</span>
                            . You can also sign in directly from the Slack application.
                            {" "}
                            <a
                                href="https://support.foqal.io/answers/urn:fq:article:7ca9e99f6ebc4755acd94052616e1e84"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Read more
                            </a>
                            {" "}
                            to find out how.
                        </small>

                    </div>

                </div>
                <div className="w-100 w-lg-50 mt-5 mt-lg-0 flex-grow-1 flex-shrink-1 ">
                    <div className="d-flex justify-content-center flex-column col-8 col-6 mx-auto">

                        {loginUrl && (
                            <a
                                href={loginUrl}
                                className="btn btn-outline d-flex align-items-center px-3 py-3 mb-3 bg-white"
                            >
                                <IoMdLogIn size="20" className="me-4 text-primary" />
                                Sign in with SSO
                            </a>
                        )}

                        <button
                            type="button"
                            className="btn btn-outline d-flex align-items-center p-0 pe-3 login-provider-slack bg-white"
                            onClick={() => loginProvider(IdentityProvider.Slack)}
                        >
                            <img src="https://static.foqal.io/library/slack.svg" alt="Slack logo" className="me-2" />
                            <div>
                                Sign in with Slack
                            </div>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline d-flex align-items-center p-0 pe-3 login-provider-google mt-3 bg-white"
                            onClick={() => loginProvider(IdentityProvider.Google)}
                        >
                            <img
                                src="https://static.foqal.io/library/google-signin.svg"
                                alt="Google logo"
                                className=" me-2"
                            />
                            <div>
                                Sign in with Google
                            </div>
                        </button>

                    </div>

                </div>
            </div>
        </div>
    );
}
